import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { submitInquiry } from "../lib/api";
import { SERVICES } from "../data/services";

export default function BookingForm({ defaultService = "" }) {
  const [params] = useSearchParams();
  const subject = params.get("subject") || "";
  const [form, setForm] = useState({
    name: "",
    email: "",
    service: subject ? "General Inquiry" : defaultService,
    message: subject ? `${subject}\n\n` : "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service || !form.message) {
      toast.error("Please fill in every field.");
      return;
    }
    setLoading(true);
    try {
      await submitInquiry(form);
      toast.success("Inquiry sent — we'll be in touch shortly.");
      setForm({ name: "", email: "", service: defaultService, message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border-b border-[#3a3a3a] focus:border-gold py-3.5 text-white placeholder:text-zinc-600 outline-none transition-colors duration-300 font-[Manrope]";

  return (
    <form
      onSubmit={submit}
      className="bg-[#121212]/60 backdrop-blur-xl border border-white/10 p-7 sm:p-10"
      data-testid="booking-form"
    >
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
        <div>
          <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
            Your Name
          </label>
          <input
            className={inputClass}
            placeholder="Jane Collector"
            value={form.name}
            onChange={update("name")}
            data-testid="booking-name"
          />
        </div>
        <div>
          <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
            Email
          </label>
          <input
            type="email"
            className={inputClass}
            placeholder="you@email.com"
            value={form.email}
            onChange={update("email")}
            data-testid="booking-email"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
            Service
          </label>
          <select
            className={`${inputClass} appearance-none cursor-pointer`}
            value={form.service}
            onChange={update("service")}
            data-testid="booking-service"
          >
            <option value="" className="bg-[#121212]">
              Select a service…
            </option>
            {SERVICES.map((s) => (
              <option key={s.slug} value={s.title} className="bg-[#121212]">
                {`${s.brandLabel} — ${s.title}`}
              </option>
            ))}
            <option value="General Inquiry" className="bg-[#121212]">
              General Inquiry
            </option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
            Message
          </label>
          <textarea
            rows={4}
            className={`${inputClass} resize-none`}
            placeholder="Tell us about your cards, stream, or booking…"
            value={form.message}
            onChange={update("message")}
            data-testid="booking-message"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="group mt-9 inline-flex items-center gap-3 rounded-full bg-gold text-black font-mono text-xs uppercase tracking-[0.15em] px-7 py-4 font-semibold hover:bg-[#d59e19] transition-colors duration-300 disabled:opacity-60"
        data-testid="booking-submit"
      >
        {loading ? (
          <>
            <Loader2 size={16} className="animate-spin" /> Sending…
          </>
        ) : (
          <>
            Send Inquiry
            <ArrowRight
              size={16}
              className="group-hover:translate-x-1 transition-transform duration-300"
            />
          </>
        )}
      </button>
    </form>
  );
}
